import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle,
} from "@phosphor-icons/react";
import AutoScrollRow from "@/subdomains/components/AutoScrollRow";
import SubdomainLayout from "@/subdomains/SubdomainLayout";
import { useSubdomainPaths } from "@/subdomains/useContactPath";

export default function SubdomainSite({ config }) {
  const { theme, icon: Icon } = config;

  return (
    <SubdomainLayout config={config} testId={`subdomain-site-${config.slug}`}>
      <SubdomainHero config={config} theme={theme} Icon={Icon} />
      <SubdomainAbout config={config} theme={theme} />
      <SubdomainModules config={config} theme={theme} />
      <SubdomainSolutions config={config} theme={theme} />
      <SubdomainWhyChoose config={config} theme={theme} />
      <SubdomainCta config={config} theme={theme} />
      <SubdomainContact config={config} theme={theme} />
    </SubdomainLayout>
  );
}

function SubdomainActionButtons({ paths, theme, className = "", centered = false, onDark = false }) {
  return (
    <div className={`flex flex-wrap gap-4 ${centered ? "justify-center" : ""} ${className}`}>
      <Link
        to={paths.contact}
        data-testid="subdomain-request-demo-cta"
        className={`inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold transition-colors shadow-lg ${
          onDark
            ? "bg-amber-400 text-zinc-900 hover:bg-amber-300"
            : `text-white ${theme.primary} ${theme.primaryHover}`
        }`}
      >
        Request Demo
        <ArrowRight size={16} weight="bold" />
      </Link>
      <Link
        to={paths.contact}
        data-testid="subdomain-book-consultation-cta"
        className={`inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold transition-colors ${
          onDark
            ? "text-white border-2 border-white/60 hover:bg-white/10"
            : "text-zinc-800 bg-white border border-zinc-200 hover:border-zinc-400"
        }`}
      >
        Book Consultation
      </Link>
    </div>
  );
}

function SubdomainHero({ config, theme, Icon }) {
  const { hero } = config;
  const paths = useSubdomainPaths(config.slug);

  return (
    <section id="home" data-testid="subdomain-hero" className="relative overflow-hidden">
      <div className="absolute inset-0">
        <img src={hero.image} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/85 to-white/60" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-32">
        <div className="max-w-2xl">
          <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-sm font-medium mb-8 ${theme.badge}`}>
            <Icon size={16} weight="fill" />
            {hero.badge}
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-zinc-900">
            {hero.headline.map((line, i) => (
              <span key={line} className={i === hero.highlightLine ? theme.primaryText : ""}>
                {line}
                {i < hero.headline.length - 1 && <br />}
              </span>
            ))}
          </h1>

          <p className="mt-6 text-lg text-zinc-600 leading-relaxed max-w-xl">{hero.description}</p>

          <SubdomainActionButtons paths={paths} theme={theme} className="mt-10" />
        </div>
      </div>
    </section>
  );
}

function SubdomainAbout({ config, theme }) {
  const { about } = config;

  return (
    <section className={`${theme.cream} py-20 lg:py-28`} data-testid="subdomain-about">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-start">
          <div>
            <div className={`text-xs font-bold tracking-[0.2em] uppercase mb-4 ${theme.primaryText}`}>
              {about.label}
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-zinc-900 leading-tight">
              {about.heading}
            </h2>
          </div>
          <p className="text-lg text-zinc-600 leading-relaxed lg:pt-8">{about.body}</p>
        </div>
      </div>
    </section>
  );
}

function SubdomainModules({ config, theme }) {
  const { modules } = config;

  return (
    <section id="modules" className="py-20 lg:py-28 bg-white" data-testid="subdomain-modules">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className={`text-xs font-bold tracking-[0.2em] uppercase mb-4 ${theme.primaryText}`}>
            {modules.label}
          </div>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-zinc-900">{modules.heading}</h2>
          <p className="mt-4 text-zinc-600 leading-relaxed">{modules.body}</p>
        </div>

        <AutoScrollRow speed={0.35}>
          {modules.items.map((mod) => {
            const ModIcon = mod.icon;
            return (
              <div
                key={mod.title}
                data-title={mod.title}
                className="flex-shrink-0 w-72 sm:w-80 bg-white border border-zinc-100 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${mod.iconBg}`}>
                  <ModIcon size={24} weight="duotone" />
                </div>
                <h3 className="text-lg font-bold text-zinc-900 mb-2">{mod.title}</h3>
                <p className="text-sm text-zinc-600 leading-relaxed">{mod.body}</p>
              </div>
            );
          })}
        </AutoScrollRow>
      </div>
    </section>
  );
}

function SubdomainSolutions({ config, theme }) {
  const { solutions } = config;
  const paths = useSubdomainPaths(config.slug);

  return (
    <section id="solutions" className={`${theme.cream} py-20 lg:py-28`} data-testid="subdomain-solutions">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="mb-14">
          <div className={`text-xs font-bold tracking-[0.2em] uppercase mb-4 ${theme.primaryText}`}>
            {solutions.label}
          </div>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-zinc-900">{solutions.heading}</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {solutions.items.map((item) => (
            <div
              key={item.title}
              className="bg-white rounded-3xl overflow-hidden border border-zinc-100 shadow-sm hover:shadow-lg transition-shadow"
            >
              <div className="relative h-48 overflow-hidden">
                <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                <div className={`absolute top-4 left-4 w-10 h-10 rounded-xl ${theme.primary} flex items-center justify-center`}>
                  <CheckCircle size={20} weight="fill" className="text-white" />
                </div>
              </div>
              <div className="p-8">
                <h3 className="text-xl font-bold text-zinc-900 mb-4">{item.title}</h3>
                <ul className="space-y-3">
                  {item.points.map((point) => (
                    <li key={point} className="flex items-start gap-3 text-sm text-zinc-600">
                      <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${theme.primary}`} />
                      {point}
                    </li>
                  ))}
                </ul>
                <Link
                  to={paths.contact}
                  className={`inline-flex items-center gap-1 mt-6 text-sm font-semibold ${theme.primaryText} hover:underline`}
                >
                  Explore {item.title}
                  <ArrowRight size={14} weight="bold" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SubdomainWhyChoose({ config, theme }) {
  const { whyChoose } = config;

  return (
    <section className="py-20 lg:py-28 bg-white" data-testid="subdomain-why-choose">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="mb-14">
          <div className={`text-xs font-bold tracking-[0.2em] uppercase mb-4 ${theme.primaryText}`}>
            {whyChoose.label}
          </div>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-zinc-900">{whyChoose.heading}</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div className="rounded-3xl overflow-hidden shadow-xl border border-zinc-100">
            <img src={whyChoose.image} alt="Analytics dashboard" className="w-full h-auto" />
          </div>
          <div className="space-y-4">
            {whyChoose.items.map((item) => (
              <div
                key={item}
                className="flex items-start gap-4 bg-white border border-zinc-100 rounded-2xl p-5 shadow-sm"
              >
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <CheckCircle size={20} weight="fill" className="text-amber-600" />
                </div>
                <p className="text-zinc-700 font-medium leading-relaxed pt-1.5">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function SubdomainCta({ config, theme }) {
  const paths = useSubdomainPaths(config.slug);

  return (
    <section
      data-testid="subdomain-cta"
      className={`py-20 lg:py-28 bg-gradient-to-br ${theme.heroGradient} text-white text-center`}
    >
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-tight">
          Ready to Transform Your {config.shortName} Operations?
        </h2>
        <p className="mt-6 text-lg text-white/85 leading-relaxed">
          Discover how {config.name} can modernize your operations into a connected, intelligent ecosystem.
        </p>
        <SubdomainActionButtons
          paths={paths}
          theme={theme}
          className="mt-10"
          centered
          onDark
        />
      </div>
    </section>
  );
}

function SubdomainContact({ config, theme }) {
  const paths = useSubdomainPaths(config.slug);

  return (
    <section id="contact" className={`${theme.cream} py-20 lg:py-28`} data-testid="subdomain-contact">
      <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
        <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-zinc-900 leading-tight">
          Let&apos;s Design Your {config.shortName} Solution
        </h2>
        <p className="mt-4 text-zinc-600 leading-relaxed">
          Share your requirements and our team will respond with a tailored demo and consultation.
        </p>
        <SubdomainActionButtons paths={paths} theme={theme} className="mt-10" centered />
      </div>
    </section>
  );
}
